import * as React from 'react'
import { XIcon } from 'lucide-react'
import { Dialog as DialogPrimitive } from 'radix-ui'

import { cn } from '@/shared/lib/utils'

import { Button } from './button'

const Modal = ({
	...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) => (
	<DialogPrimitive.Root data-slot='Modal' {...props} />
)

const ModalTrigger = ({
	...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) => (
	<DialogPrimitive.Trigger data-slot='modal-trigger' {...props} />
)

const ModalPortal = ({
	...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) => (
	<DialogPrimitive.Portal data-slot='modal-portal' {...props} />
)

const ModalClose = ({
	...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) => (
	<DialogPrimitive.Close data-slot='modal-close' {...props} />
)

const ModalOverlay = ({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) => (
	<DialogPrimitive.Overlay
		data-slot='modal-overlay'
		className={cn(
			'animate-fade-in fixed inset-0 z-50 grid place-items-center overflow-auto bg-zinc-800/50 fill-mode-forwards data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
			className
		)}
		{...props}
	/>
)

const ModalContent = ({
	className,
	children,
	hideCloseButton,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
	hideCloseButton?: boolean
}) => (
	<ModalPortal data-slot='modal-portal'>
		<ModalOverlay>
			<DialogPrimitive.Content
				data-slot='modal-content'
				className={cn(
					'z-50 grid w-full max-w-[calc(100%-2rem)] max-w-md gap-4 rounded-xl border bg-background p-4 shadow-lg duration-200 fill-mode-forwards data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
					className
				)}
				// workaround for known issue https://github.com/shadcn-ui/ui/issues/1859
				onCloseAutoFocus={(event) => {
					event.preventDefault()
					document.body.style.pointerEvents = ''
				}}
				{...props}
			>
				{children}
				{!hideCloseButton && (
					<DialogPrimitive.Close
						asChild
						className='absolute top-4 right-4'
						data-testid='modal-close-button'
					>
						<Button size='icon-sm' variant='ghost'>
							<XIcon />
						</Button>
					</DialogPrimitive.Close>
				)}
			</DialogPrimitive.Content>
		</ModalOverlay>
	</ModalPortal>
)

const ModalHeader = ({ className, ...props }: React.ComponentProps<'div'>) => (
	<div
		data-slot='modal-header'
		className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
		{...props}
	/>
)

const ModalFooter = ({ className, ...props }: React.ComponentProps<'div'>) => (
	<div
		data-slot='modal-footer'
		className={cn(
			'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
			className
		)}
		{...props}
	/>
)
const ModalBody = ({ className, ...props }: React.ComponentProps<'div'>) => (
	<div
		data-slot='modal-body'
		className={cn('relative -mx-4 flex-auto px-4', className)}
		{...props}
	/>
)

const ModalTitle = ({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) => (
	<DialogPrimitive.Title
		data-slot='modal-title'
		className={cn('text-lg leading-none font-semibold', className)}
		{...props}
	/>
)

const ModalDescription = ({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) => (
	<DialogPrimitive.Description
		data-slot='modal-description'
		className={cn('text-sm text-muted-foreground', className)}
		{...props}
	/>
)
const ModalCloseButton = ({
	...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) => (
	<DialogPrimitive.Close asChild className='absolute top-4 right-4' {...props}>
		<Button variant='ghost' size='icon-sm'>
			<XIcon />
		</Button>
	</DialogPrimitive.Close>
)

export {
	Modal,
	ModalBody,
	ModalClose,
	ModalCloseButton,
	ModalContent,
	ModalDescription,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	ModalPortal,
	ModalTitle,
	ModalTrigger,
}
